import { Point2D } from 'Common/types/generic/Points'
import { DefectTypes, DefectCategories } from 'Modules/types/pages/imageReview/enums'
import {
  DAMAGE_TYPE_STROKE_COLOR,
  MARKER_STROKE_COLOR,
  MARKER_SELECTED_STROKE_COLOR,
  MARKER_MEASUREMENT_STROKE_COLOR,
  DEFECT_CATEGORY_LOOKUP,
} from './constants'
import { ImageAnnotation, ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
import {
  BaseMarker,
  MarkerModes,
  RectangleMarkerState,
  PolygonMarkerState,
  MarkerState,
  BezierLineMarkerState,
} from 'Common/utils/markerArea/types'
import { MarkerArea } from 'Common/utils/markerArea/markerArea'
import { MarkerTypes } from 'Modules/types/pages/imageReview/enums'
import { ImageMeasurementType, ImageMeasurements } from 'Modules/types/pages/imageReview/imageMeasurementType'
import { formatDayByDate, formatTimeByDate } from '@/common/utils/formatDate';

/**
 * Given a set of vertices, calculate if they create a rectangle.
 * @param {number[][]} vertices
 * @returns {boolean} Returns true if the vertices form a rectangle; false otherwise
 */
function isRectangle(vertices: number[][]): boolean {
  // Quick check for 4 vertices
  if (vertices.length !== 4) {
    return false
  }

  // Calculate distance between two points
  function distance(point1, point2): number {
    return Math.sqrt(Math.pow(point2[0] - point1[0], 2) + Math.pow(point2[1] - point1[1], 2))
  }

  // Calculate all distances (4 sides and 2 diagonals)
  const distances = [
    distance(vertices[0], vertices[1]), // Side 1
    distance(vertices[1], vertices[2]), // Side 2
    distance(vertices[2], vertices[3]), // Side 3
    distance(vertices[3], vertices[0]), // Side 4
    distance(vertices[0], vertices[2]), // Diagonal 1
    distance(vertices[1], vertices[3]), // Diagonal 2
  ]

  // Check for equal opposite sides and equal diagonals
  const isOppositeSidesEqual = distances[0] === distances[2] && distances[1] === distances[3]
  const isDiagonalsEqual = distances[4] === distances[5]

  return isOppositeSidesEqual && isDiagonalsEqual
}

/**
 * Given a rectangle defined by x/y position, height, and width, return a list of vertices.
 * @param {number} x X position of the rectangle
 * @param {number} y Y position of the rectangle
 * @param {number} width Width of the rectnagle
 * @param {number} height Height of the rectangle
 * @returns {number[][]} Array of Array of points defining the vertices of the rectangle.
 */
function getRectangleVertices(x: number, y: number, width: number, height: number): number[][] {
  // Calculate vertices
  const top_left = [x, y]
  const top_right = [x + width, y]
  const bottom_right = [x + width, y + height]
  const bottom_left = [x, y + height]

  // Return the array of vertices
  return [top_left, top_right, bottom_right, bottom_left]
}

/**
 * Translates from array of point definition to an array of array definition to represent vertices.
 * @param {Array<Point2D>} points Array of 2D points
 * @returns {number[][]} Array of array of points defining the vertices of the given points.
 */
function getPointsVertices(points: Array<Point2D>) {
  const vertices = []
  points.forEach((point) => {
    vertices.push([point.x, point.y])
  })
  return vertices
}

/**
 * Given an array of array of numbers, translates to an array of 2D points defiend by x and y values.
 * @param {number[][]} vertices Array of array of numbers representing vertices of a shape.
 * @returns {Array<Point2D>} Array of 2D points representing vertices of a shape.
 */
function getPointsFromVerticies(vertices: number[][]): Array<Point2D> {
  const points = []
  vertices.forEach((vertex) => {
    points.push({ x: vertex[0], y: vertex[1] })
  })
  return points
}

/**
 * Given an array of array of numbers, which represent the vertices of a rectangle, return a rectangle definiton given by x/y position, height, and width
 * @param {number[][]} vertices
 * @returns Rectangle definition
 */
function getRectangleDefinitionFromVerticies(vertices: number[][]): {
  dimensions: {
    width: number
    height: number
  }
  position: Point2D
} {
  const dimensions = { height: null, width: null }
  const position = { x: null, y: null }
  const minX = Math.min(...vertices.map((vertex) => vertex[0]))
  const maxX = Math.max(...vertices.map((vertex) => vertex[0]))
  const minY = Math.min(...vertices.map((vertex) => vertex[1]))
  const maxY = Math.max(...vertices.map((vertex) => vertex[1]))

  dimensions.width = maxX - minX
  dimensions.height = maxY - minY

  position.x = minX
  position.y = minY

  return {
    dimensions,
    position,
  }
}

/**
 * Gets the defect category of the provided defect type.
 * @param {DefectTypes} defect_type
 * @returns The category the defect type falls under
 */
const getDefectCategory = (defect_type: DefectTypes): string => {
  return DEFECT_CATEGORY_LOOKUP[defect_type]
}

/**
 * Gets the defect color for the provided defect type.
 * @param {DefectTypes} defect_type
 * @returns {string} The color assigned to a defect type based off of it's calculated defeect category.
 */
const getDefectStrokeColor = (defectType: DefectTypes): string => {
  const defect_category = getDefectCategory(defectType)
  const defect_stroke_color = DAMAGE_TYPE_STROKE_COLOR[defect_category]
  return defect_stroke_color || MARKER_STROKE_COLOR
}

/**
 * Gets a list of all defect types that fall under the provided defect category.
 * @param {DefectCategories} category
 * @returns List of defect types that fall under the provided category
 */
const getDefectTypesByCategory = (category: DefectCategories): DefectTypes[] => {
  const types: DefectTypes[] = []
  for (const [type, cat] of Object.entries(DEFECT_CATEGORY_LOOKUP)) {
    if (cat === category) {
      types.push(type as DefectTypes)
    }
  }
  return types
}

// Function to generate options of defects grouped by category
const getDefectTypeSelectOptions = (): { label: string; options: { value: string; label: string }[] }[] => {
  const categories: Record<DefectCategories, { value: string; label: string }[]> = {} as any
  // Initialize categories
  Object.values(DefectCategories).forEach((category) => {
    categories[category as DefectCategories] = []
  })

  // Group types by category
  Object.entries(DefectTypes).forEach(([key, type]) => {
    const category = DEFECT_CATEGORY_LOOKUP[type]
    if (categories[category]) {
      categories[category].push({ value: key, label: type })
    }
  })

  // Map to options format
  return Object.entries(categories).map(([category, options]) => ({
    label: category.replace(/_/g, ' '),
    options,
  }))
}

function generateMarkerStateFromAnnotation(annotation: ImageAnnotation) {
  const defectBounds = annotation.defectBounds
  const uuid = annotation.annotationUuid
  const isSuggestion = annotation.isAnnotation === false
  const isRectangleAnnotation = isRectangle(defectBounds)
  const mappedDefectValue = DefectTypes[annotation.defectType]
  const defectStrokeColor = getDefectStrokeColor(mappedDefectValue)

  let markerState
  if (isRectangleAnnotation) {
    const rectangleDefinition = getRectangleDefinitionFromVerticies(defectBounds)
    markerState = {
      ...rectangleDefinition,
      typeName: MarkerTypes.Rectangle,
      mode: MarkerModes.Display,
      strokeColor: defectStrokeColor,
      isDashed: isSuggestion,
      meta: { id: uuid, strokeCategoryColor: defectStrokeColor, isSuggestion: isSuggestion },
    } as RectangleMarkerState
  } else {
    const polygonDefinition = getPointsFromVerticies(defectBounds)
    markerState = {
      points: polygonDefinition,
      typeName: MarkerTypes.Polygon,
      mode: MarkerModes.Display,
      strokeColor: defectStrokeColor,
      isDashed: isSuggestion,
      meta: { id: uuid, strokeCategoryColor: defectStrokeColor, isSuggestion: isSuggestion },
    } as PolygonMarkerState
  }
  return markerState
}

/**
 * Takes in Image Annotations, converts them into a corresponding marker object based on it's bounds,
 * and returns said markers in an array
 * @param annotations Array of ImageAnnotation Type
 * @returns Array of MarkerTypes
 */
function generateMarkerStateFromAnnotations(annotations: Array<ImageAnnotation>) {
  const markersState = []
  annotations?.forEach((annotation: ImageAnnotation) => {
    const markerState = generateMarkerStateFromAnnotation(annotation)
    markersState.push(markerState)
  })
  return markersState
}

function generateMarkerStateFromMeasurement(measurement: ImageMeasurementType) {
  const anchorPoints = measurement.points?.map((pointArray) => ({
    x: pointArray[0],
    y: pointArray[1],
  }))
  const metaProps = {
    isMeasurement: true,
    annotationUuid: measurement.annotationUuid,
    id: measurement.measurementUuid,
  }
  const markerState: BezierLineMarkerState = {
    anchorPoints: anchorPoints,
    controlPoints: [],
    typeName: MarkerTypes.Measurement,
    mode: MarkerModes.Display,
    strokeColor: MARKER_MEASUREMENT_STROKE_COLOR,
    meta: metaProps,
    opacity: 95,
  } as BezierLineMarkerState
  return markerState
}

function generateMarkerStateFromMeasurements(measurements: ImageMeasurements) {
  const markersState: Array<MarkerState> = []
  measurements
    .filter((measurement: ImageMeasurementType) => Array.isArray(measurement.points))
    .forEach((measurement: ImageMeasurementType) => {
      const markerState = generateMarkerStateFromMeasurement(measurement)
      markersState.push(markerState)
    })
  return markersState
}

/**
 * Syncs a ImageAnnotation udpate with the current marker area marker
 * by updating state of the markers.
 * @param annotations
 * @param markerArea
 */
function syncAnnotationBoundUpdate(annotations: Array<ImageAnnotation>, markerArea: MarkerArea) {
  const markers = markerArea.markers
  annotations.forEach((annotation) => {
    const matchingMarker = markers.find((marker) => marker.state.meta.id === annotation.annotationUuid)
    if (matchingMarker) {
      const defectBounds = annotation.defectBounds
      const mappedDefectValue = DefectTypes[annotation.defectType]
      const defectStrokeColor = getDefectStrokeColor(mappedDefectValue)
      if (matchingMarker.typeName === MarkerTypes.Rectangle) {
        const rectangleDefinition = getRectangleDefinitionFromVerticies(defectBounds)
        matchingMarker.state.dimensions = rectangleDefinition.dimensions
        matchingMarker.state.position = rectangleDefinition.position
      } else if (matchingMarker.typeName === MarkerTypes.Polygon) {
        const polygonDefinition = getPointsFromVerticies(defectBounds)
        matchingMarker.state.points = polygonDefinition
      }
      matchingMarker.state.isSelected
        ? (matchingMarker.state.strokeColor = MARKER_SELECTED_STROKE_COLOR)
        : (matchingMarker.state.strokeColor = defectStrokeColor)
      matchingMarker.state.meta.strokeCategoryColor = defectStrokeColor
    }
  })
}

/**
 * Syncs a ImageAnnotation deletion with the current marker area marker state by removing any markers that
 * not have a matching uuid meta field with a ImageAnnotation
 * @param annotations
 * @param markerArea
 */
function syncAnnotationDelete(annotations: Array<ImageAnnotation>, markerArea: MarkerArea): void {
  markerArea.currentMarker = null
  markerArea.markers = markerArea.markers.filter((marker) => {
    const hasMatchingAnnotation = annotations.some((annotation) => annotation.annotationUuid === marker.state.meta.id)
    return hasMatchingAnnotation
  })
}

/**
 * Syncs the temporary marker state based on the return of a succesfully saved annotation.
 * @param annotation ImageAnnotation Type
 * @param tempMarker BaseMarker or derivative types
 */
function syncTempMarker(annotation: ImageAnnotationType, tempMarker: BaseMarker<any, any, any, any>) {
  const defect_type = DefectTypes[annotation.defectType]
  const defect_stroke_color = getDefectStrokeColor(defect_type)
  tempMarker.state.meta.id = annotation.annotationUuid
  tempMarker.state.meta.strokeCategoryColor = defect_stroke_color
  tempMarker.state.strokeColor = defect_stroke_color
}

/**
 * Finds the first marker with a substring of temp stored within it's id meta field
 * @param markers
 * @returns
 */
function findTempMarker(markers: Array<BaseMarker<any, any, any, any>>) {
  return markers?.find((marker) => marker.state.meta?.id?.includes('temp'))
}

/**
 * Finds markers with matching id stored within it's meta fields
 * @param {string} id String to match against
 * @param {BaseMarker} markers Array of BaseMarker or it's decendants
 * @returns {BaseMarker | undefined} BaseMarker or it's decendents
 */
function findMarkerById(id: string, markers: Array<BaseMarker<any, any, any, any>>) {
  return markers?.find((marker) => marker.state.meta?.id === id)
}

/**
 * Gets all markers with a meta prop of isMeasurement not equal to true
 * @param markers BaseMarker from markerarea or any decendents
 * @returns List of BaseMarker or decendents
 */
function getDefectMarkers(markers: Array<BaseMarker<any, any, any, any>>) {
  return markers.filter((marker) => {
    marker.state.meta.isMeasurement !== true
  })
}

/**
 * Gets all markers with a meta prop of isMeasurement equal to true
 * @param markers BaseMarker from markerarea or any decendents
 * @returns List of BaseMarker or decendents
 */
function getMeasurementMarkers(markers: Array<BaseMarker<any, any, any, any>>) {
  return markers.filter((marker) => {
    marker.state.meta.isMeasurement === true
  })
}

function getSortedInspectionDates(inspections: [{ startDate: string }], order = 'asc') {
  const sortedDates = Object.assign(JSON.parse(JSON.stringify(inspections)), {}).sort((a: { startDate: string }, b: { startDate: string }) => {
    const dateA = new Date(a.startDate).getTime();
    const dateB = new Date(b.startDate).getTime();
    if (order === 'asc') return dateA > dateB ? 1 : -1;
    return dateA < dateB ? 1 : -1;
  })
  return sortedDates
}

function getGroupedAnnotationsMap(confirmedAnnotations) {
  const grouped = confirmedAnnotations.reduce(
    (acc, annotation) => {
      if (annotation.annotationGroup) {
        const key = annotation.annotationGroup
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(annotation)
      } else {
        acc.ungrouped.push(annotation)
      }
      return acc
    },
    { ungrouped: [] }
  )
  return grouped
}

function getGroupedAnnotationsArray(groupedAnnotationsMap) {
  const grouped = groupedAnnotationsMap
  const result = [
    ...Object.values(grouped)
      .filter((group) => group !== grouped.ungrouped)
      .map((group) => {
        return group.length > 0 ? group : group[0]
      }),
    ...grouped.ungrouped,
  ]
  return result
}

export {
  getDefectTypeSelectOptions,
  getDefectCategory,
  getDefectStrokeColor,
  getDefectTypesByCategory,
  getPointsFromVerticies,
  getPointsVertices,
  getRectangleVertices,
  getRectangleDefinitionFromVerticies,
  getSortedInspectionDates,
  getGroupedAnnotationsMap,
  getGroupedAnnotationsArray,
  isRectangle,
  generateMarkerStateFromAnnotation,
  generateMarkerStateFromAnnotations,
  generateMarkerStateFromMeasurements,
  syncAnnotationBoundUpdate,
  syncAnnotationDelete,
  findTempMarker,
  findMarkerById,
  getDefectMarkers,
  getMeasurementMarkers,
  syncTempMarker,
}
